import { LDFlagSet } from 'launchdarkly-react-client-sdk';
import { useEffect, useState } from 'react';
import { PatientTableColName, VisibleColumnsConfigType, PATIENTS_SORT_STORAGE_KEY } from '../PatientTable/PatientTableTypes';

const DEFAULT_COLUMNS_CONFIG: VisibleColumnsConfigType = {
  [PatientTableColName.PATIENT_TYPE]: {
    key: PatientTableColName.PATIENT_TYPE,
    title: 'Patient type',
    visible: true
  },
  [PatientTableColName.HEART_RATE]: {
    key: PatientTableColName.HEART_RATE,
    title: 'Heart rate (bpm)',
    visible: true
  },
  [PatientTableColName.BREATHING_RATE]: {
    key: PatientTableColName.BREATHING_RATE,
    title: 'Breathing rate (bpm)',
    visible: true
  },
  [PatientTableColName.SLEEP_DURATION]: {
    key: PatientTableColName.SLEEP_DURATION,
    title: 'Sleep duration',
    visible: true
  },
  [PatientTableColName.SLEEP_EFFICIENCY]: {
    key: PatientTableColName.SLEEP_EFFICIENCY,
    title: 'Sleep efficiency',
    visible: false
  },
  [PatientTableColName.DEVICE_MODE]: {
    key: PatientTableColName.DEVICE_MODE,
    title: 'Device mode',
    visible: true,
    canSort: true
  },
  [PatientTableColName.PERSON_PRESENCE]: {
    key: PatientTableColName.PERSON_PRESENCE,
    title: 'Patient presence',
    visible: true,
    canSort: true
  },
  [PatientTableColName.DEVICE_NUMBER]: {
    key: PatientTableColName.DEVICE_NUMBER,
    title: 'Device number',
    visible: true,
    canSort: true
  },
  [PatientTableColName.DEVICE_STATUS]: {
    key: PatientTableColName.DEVICE_STATUS,
    title: 'Device status',
    visible: true,
    canSort: true
  }
};

const ALZHEIMERS_SLEEP_STUDY_DEFAULT_COLUMNS_STUDY_COORDINATOR = {
  [PatientTableColName.DEVICE_MODE]: {
    key: PatientTableColName.DEVICE_MODE,
    title: 'Device mode',
    visible: true,
    canSort: true
  },
  [PatientTableColName.PERSON_PRESENCE]: {
    key: PatientTableColName.PERSON_PRESENCE,
    title: 'Patient presence',
    visible: true,
    canSort: true
  },
  [PatientTableColName.DEVICE_NUMBER]: {
    key: PatientTableColName.DEVICE_NUMBER,
    title: 'Device number',
    visible: true,
    canSort: true
  },
  [PatientTableColName.DEVICE_STATUS]: {
    key: PatientTableColName.DEVICE_STATUS,
    title: 'Device status',
    visible: true,
    canSort: true
  },
  [PatientTableColName.NIGHT_SLEEP_TIME]: {
    key: PatientTableColName.NIGHT_SLEEP_TIME,
    title: 'Sleep Duration',
    visible: true,
    canSort: true
  }
};

const ALZHEIMERS_SLEEP_STUDY_DEFAULT_COLUMNS_CAREGIVER = {
  [PatientTableColName.NIGHT_SLEEP_TIME]: {
    key: PatientTableColName.NIGHT_SLEEP_TIME,
    title: 'Sleep Duration',
    visible: true,
    canSort: true
  },
  [PatientTableColName.NIGHT_AWAKE_TIME]: {
    key: PatientTableColName.NIGHT_AWAKE_TIME,
    title: 'Awakenings',
    visible: true,
    canSort: true
  },
  [PatientTableColName.BED_EXITS]: {
    key: PatientTableColName.BED_EXITS,
    title: 'Bed Exits',
    visible: true,
    canSort: true
  },
  [PatientTableColName.DAY_SLEEP_TIME]: {
    key: PatientTableColName.DAY_SLEEP_TIME,
    title: 'Daytime Sleep Duration',
    visible: true,
    canSort: true
  },
  [PatientTableColName.DEVICE_NUMBER]: {
    key: PatientTableColName.DEVICE_NUMBER,
    title: 'Device number',
    visible: true,
    canSort: true
  },
  [PatientTableColName.DEVICE_STATUS]: {
    key: PatientTableColName.DEVICE_STATUS,
    title: 'Device status',
    visible: true,
    canSort: true
  }
};

export const PATIENTS_COLUMNS_STORAGE_KEY = 'patients_table_config';

export const useCustomizeColumnsConfig = (userId: string = '', flags: LDFlagSet) => {
  const saved = localStorage.getItem(PATIENTS_COLUMNS_STORAGE_KEY);
  let parsedSaved = saved ? JSON.parse(saved) : null;

  const default_columns = flags.alzheimersSleepStudy
    ? flags.alzheimersSleepStudyCaregiver
      ? ALZHEIMERS_SLEEP_STUDY_DEFAULT_COLUMNS_CAREGIVER
      : ALZHEIMERS_SLEEP_STUDY_DEFAULT_COLUMNS_STUDY_COORDINATOR
    : DEFAULT_COLUMNS_CONFIG;

  const [columnsConfig, setColumnsConfig] = useState<VisibleColumnsConfigType>(() => {
    //update condition to clear out-of-date config and reset to default columns
    // (keyed on DEVICE_NUMBER: caregiver configs saved before the caregiver column
    // change lack it, so they reset once and pick up the new Device number column)
    if ((saved && !localStorage.getItem(PATIENTS_SORT_STORAGE_KEY)) || !parsedSaved?.[userId]?.[PatientTableColName.DEVICE_NUMBER]) {
      localStorage.removeItem(PATIENTS_COLUMNS_STORAGE_KEY);
      parsedSaved = null;
      return default_columns;
    }

    return userId && parsedSaved?.[userId] ? parsedSaved[userId] : default_columns;
  });

  useEffect(() => {
    if (!userId) return;

    const newColumnsConfig = parsedSaved ? { ...parsedSaved, [userId]: columnsConfig } : { [userId]: columnsConfig };
    localStorage.setItem(PATIENTS_COLUMNS_STORAGE_KEY, JSON.stringify(newColumnsConfig));
  }, [parsedSaved, columnsConfig]);

  const toggleColumn = (key: PatientTableColName) => {
    setColumnsConfig((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        visible: Boolean(!prev[key]?.visible)
      }
    }));
  };

  return {
    columnsConfig,
    toggleColumn
  };
};
